import * as Net from 'util/Net';

export interface OnboardingSessionResult {
  session_id: string;
}

export interface AboutMeResponse {
  content: string | null;
}

/** A starter-prompt suggestion: a short label and the prompt text submitted as the first chat. */
export interface OnboardingPromptSuggestion {
  label: string;
  prompt: string;
}

/** An existing starter prompt the backend can choose from, tagged with its stable id. */
export interface OnboardingExamplePrompt extends OnboardingPromptSuggestion {
  id: string;
}

interface SuggestPromptsResponse {
  suggestions: Array<OnboardingExamplePrompt>;
}

/**
 * Start or resume the onboarding question flow for the current user.
 *
 * Creates a new onboarding session if the user has never onboarded, resumes
 * an existing session that is still waiting for answers, or creates a fresh
 * session if the previous one is already complete (restart).
 */
export function startOnboardingSession(): Promise<OnboardingSessionResult> {
  return Net.postJSONPromise({
    url: '/1/api/users/me/llm-onboarding/start',
    data: {},
  });
}

/**
 * Restart the onboarding flow for the current user.
 * Clears the existing onboarding session so the next start creates a fresh one.
 */
export async function restartOnboardingSession(): Promise<void> {
  await Net.postJSONPromise({url: '/1/api/users/me/llm-onboarding/restart', data: {}});
}

/**
 * Fetch the current user's About Me paragraph generated from the onboarding flow.
 */
export async function getUserAboutMe(): Promise<string | null> {
  const response: AboutMeResponse = await Net.getJSONPromise('/1/api/users/me/llm-onboarding/about-me');
  return response.content;
}

/**
 * Clear the current user's About Me paragraph.
 */
export async function clearUserAboutMe(): Promise<void> {
  await Net.deletePromise({url: '/1/api/users/me/llm-onboarding/about-me'});
}

/**
 * Pick the 3 starter prompts most relevant to the user's free-text onboarding interest answer.
 *
 * Sends the user's answer along with the static progressive-disclosure prompts (each tagged with its
 * stable id); the backend asks the LLM to select the 3 most relevant by id and returns them verbatim,
 * including the id so callers can map a chosen prompt back to its progressive-disclosure category.
 * Returns an empty array if the model fails or produces nothing usable, so callers can fall back to the
 * static category pickers.
 */
export async function suggestOnboardingPrompts(
  userAnswer: string,
  examplePrompts: Array<OnboardingExamplePrompt>
): Promise<Array<OnboardingExamplePrompt>> {
  const response: SuggestPromptsResponse = await Net.postJSONPromise({
    url: '/1/api/users/me/llm-onboarding/suggest-prompts',
    data: {userAnswer, examplePrompts},
  });
  return response.suggestions ?? [];
}

/**
 * Record the user's onboarding interest answer, folding it into their About Me personalization.
 */
export async function recordOnboardingInterest(interest: string): Promise<void> {
  await Net.postJSONPromise({
    url: '/1/api/users/me/llm-onboarding/record-interest',
    data: {interest},
  });
}
